import React, {
  createContext,
  type CSSProperties,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Radio as BaseRadio } from '@base-ui/react/radio';
import { RadioGroup as BaseRadioGroup } from '@base-ui/react/radio-group';
import { cn } from '@opendoor/atoms/utils';
import { useIsomorphicLayoutEffect } from 'components/shared/hooks/useIsomorphicLayoutEffect';

import { useObservability } from '../../../helpers/observability';

type SegmentedControlSize = 'md' | 'sm';

const SIZE_CLASSES: Record<SegmentedControlSize, string> = {
  sm: 'h-14 rounded-2xl p-1',
  md: 'h-16 rounded-2xl p-1',
};

type SegmentedControlProps = {
  children: ReactNode;
  /** Accessible name for the group; the control renders no visible label of its own. */
  'aria-label': string;
  /**
   * Value of the selected segment (controlled). Base UI fixes controlled vs
   * uncontrolled mode at first render: never cross between a defined `value`
   * and `undefined` after mount — later changes are silently ignored.
   */
  value?: string;
  /** Initially selected segment value (uncontrolled). Never read again. */
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  className?: string;
  size?: SegmentedControlSize;
  /** Names this control in journey events; unset fires nothing. */
  analyticsName?: string;
};

// Until the pill has measured (SSR first paint, or no checked segment), the
// checked segment carries the selection background itself.
const PillVisibleContext = createContext(false);

const SegmentedControlRoot = ({
  children,
  size = 'md',
  value,
  defaultValue,
  onValueChange,
  disabled,
  className,
  analyticsName,
  ...ariaProps
}: SegmentedControlProps) => {
  const { trackJourneyEvent } = useObservability();
  const groupRef = useRef<HTMLDivElement>(null);
  const [pill, setPill] = useState<{ width: number; x: number } | null>(null);

  // Segments are content-sized, so the pill tracks the checked segment's real
  // geometry, measured subpixel-precise against the group's box. The observer
  // re-measures on font load, resize, and label changes. A zero-width measure
  // (no layout yet) keeps the fallback instead of mounting an invisible pill.
  const measure = useCallback(() => {
    const group = groupRef.current;
    const active = group?.querySelector<HTMLElement>('[data-checked]');
    if (!group || !active) {
      setPill(null);
      return;
    }
    const groupRect = group.getBoundingClientRect();
    const rect = active.getBoundingClientRect();
    const next = rect.width > 0 ? { width: rect.width, x: rect.left - groupRect.left } : null;
    setPill((prev) => (next && prev?.width === next.width && prev?.x === next.x ? prev : next));
  }, []);

  useIsomorphicLayoutEffect(() => {
    const group = groupRef.current;
    if (!group) {
      return undefined;
    }
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(group);
    for (const segment of group.querySelectorAll('[role="radio"]')) {
      observer.observe(segment);
    }
    return () => observer.disconnect();
    // value: a parent-driven change (e.g. a filter reset flipping the segment)
    // moves data-checked without resizing anything, so re-measure on it.
  }, [measure, value]);

  const handleValueChange = useCallback(
    (newValue: string) => {
      if (analyticsName) {
        trackJourneyEvent('platform', 'radioClickEvent', {
          targetName: analyticsName,
          optionName: newValue,
        });
      }
      // Uncontrolled groups do not re-render on selection, and the data-checked
      // move resizes nothing — measure after Base UI lands the DOM change.
      requestAnimationFrame(measure);
      onValueChange?.(newValue);
    },
    [analyticsName, trackJourneyEvent, measure, onValueChange],
  );

  const pillStyle = useMemo<CSSProperties | undefined>(
    () =>
      pill
        ? {
            transform: `translateX(${pill.x}px)`,
            width: pill.width,
          }
        : undefined,
    [pill],
  );

  return (
    <PillVisibleContext.Provider value={pillStyle != null}>
      <BaseRadioGroup
        ref={groupRef}
        value={value}
        defaultValue={defaultValue}
        onValueChange={handleValueChange}
        disabled={disabled}
        className={cn('relative inline-flex bg-od-bg-tertiary', SIZE_CLASSES[size], className)}
        {...ariaProps}
      >
        {pillStyle && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-1 left-0 rounded-xl bg-od-content-primary shadow-sm transition-[transform,width] duration-200 ease-out"
            style={pillStyle}
          />
        )}
        {children}
      </BaseRadioGroup>
    </PillVisibleContext.Provider>
  );
};

SegmentedControlRoot.displayName = 'SegmentedControl';

type SegmentedControlItemProps = {
  /** Value reported to the group's onValueChange when selected */
  value: string;
  /** Visible segment label */
  children: ReactNode;
  className?: string;
};

const SegmentedControlItem = ({ value, children, className }: SegmentedControlItemProps) => {
  const pillVisible = useContext(PillVisibleContext);

  return (
    <BaseRadio.Root
      value={value}
      className={cn(
        'relative z-1 flex cursor-pointer items-center justify-center rounded-xl px-3 text-label-md whitespace-nowrap transition-colors',
        'text-od-content-secondary hover:text-od-content-primary',
        'data-checked:text-od-content-inverse-primary',
        'focus-visible:ring-2 focus-visible:ring-od-border-focused-accent-brand',
        !pillVisible && 'data-checked:bg-od-content-primary data-checked:shadow-sm',
        'data-disabled:cursor-not-allowed data-disabled:opacity-50',
        className,
      )}
    >
      {children}
    </BaseRadio.Root>
  );
};

SegmentedControlItem.displayName = 'SegmentedControl.Item';

/**
 * Exclusive single-select switch rendered as a segmented track with a sliding
 * selection pill. Built on Base UI radio group for keyboard navigation and
 * radiogroup semantics.
 */
export const SegmentedControl = Object.assign(SegmentedControlRoot, {
  Item: SegmentedControlItem,
});
